// The PORTAL half of assisted completion: a broker driving a client's form from
// their own signed-in machine.
//
// A thin transport plus a back link. Every human-facing decision — the
// certification choice, the client-copy checkbox, the copy — lives once in
// AssistedPrequalSurface, shared with the staff surface, because the two must
// not be able to drift apart.

import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { PortalApiError } from "../../api/client";
import { prequalSiteApi } from "../../api/prequalSite";
import { usePortalAuth } from "../../PortalAuthContext";
import { dashboardPathFor } from "../RecipientDashboardPage";
import { AssistedPrequalSurface } from "./AssistedPrequalSurface";
import type { AssistedDraftTransport } from "./useAssistedPrequalDraftAdapter";

export function PortalAssistPrequalPage() {
  const { draftId } = useParams();
  const auth = usePortalAuth();

  // Back to the tab this was opened from, resolved from the SESSION's own
  // account type rather than a `?back=` parameter — a caller-supplied return
  // path on an authenticated surface is an open redirect waiting to happen.
  //
  // `view` is pinned to the In progress list rather than left to default,
  // because that is the only list this page can be reached from: "Continue with
  // them" opens an in-progress draft, so landing back on Completions would drop
  // the broker on a different list from the one they were working.
  const backTo = auth.me
    ? `${dashboardPathFor(auth.me.accountType, auth.me.kind)}?tab=prequalification&view=in-progress`
    : "/portal";

  const transport = useMemo<AssistedDraftTransport>(
    () => ({
      load: (id) => prequalSiteApi.assistLoad(id),
      step: (id, body) => prequalSiteApi.assistStep(id, body),
      finalize: (id, body) => prequalSiteApi.assistFinalize(id, body),
      // A conflict is a real answer the form renders as "reload and try again",
      // so it has to survive the trip out of the HTTP client intact — sniffing
      // the message text instead would degrade it into a generic failure.
      isConflict: (error) => error instanceof PortalApiError && error.status === 409,
    }),
    [],
  );

  if (!draftId) return <Navigate to={backTo} replace />;

  return (
    <AssistedPrequalSurface
      draftId={draftId}
      transport={transport}
      backTo={backTo}
      backLabel="Back to prequalification"
      // Display only — for the attestation helper, so "recorded against your
      // name" can say the name. The certifier that is actually RECORDED is
      // resolved server-side from this session and is on no request body.
      operatorName={auth.me?.fullName}
    />
  );
}
